import { useCallback, useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  getChannelImages,
  getAIReferenceImages,
  type ChannelSlot,
  type AIRefSlot,
} from '@/lib/api/products'
import { getAssetUrl } from '@/lib/helpers/asset-urls'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import type { AddToProductTarget } from '../types'

/**
 * Slot-level data the UI needs. `linkId` is the product_asset_link primary
 * key — needed for the Bundle-D save() pipeline (reorderProductImages takes
 * link-ids). `assetId` lets us construct the AddToProductImage payload when
 * the workspace forwards the occupancy snapshot to usePendingChannelDrops.add().
 */
export interface ChannelSlotFill {
  url: string
  linkId?: string
  assetId?: string
}

export interface AiRefSlotFill {
  url: string
  assetId?: string
}

export interface SlotOccupancyData {
  isOccupied: (t: AddToProductTarget) => boolean
  filledByChannel: Record<string, Record<number, ChannelSlotFill>>
  filledAiRef: Record<number, AiRefSlotFill>
}

function buildChannelMap(
  channelIds: string[],
  results: { data: ChannelSlot[] | undefined }[],
): Record<string, Record<number, ChannelSlotFill>> {
  const out: Record<string, Record<number, ChannelSlotFill>> = {}
  channelIds.forEach((id, idx) => {
    const slots = results[idx]?.data ?? []
    const filled: Record<number, ChannelSlotFill> = {}
    for (const s of slots) {
      if (!s.empty && s.asset_id) {
        filled[s.slot] = {
          url: getAssetUrl(s.asset_id, 'thumb'),
          linkId: s.id,
          assetId: s.asset_id,
        }
      }
    }
    out[id] = filled
  })
  return out
}

function buildAiRefMap(slots: AIRefSlot[] | undefined): Record<number, AiRefSlotFill> {
  const out: Record<number, AiRefSlotFill> = {}
  for (const s of slots ?? []) {
    if (!s.is_empty && s.asset_id) {
      out[s.slot] = {
        url: s.thumbnail_url ?? getAssetUrl(s.asset_id, 'thumb'),
        assetId: s.asset_id,
      }
    }
  }
  return out
}

export function useSlotOccupancy(productId: string | null): SlotOccupancyData {
  const channels = useProductChannelList(productId ?? '')
  const channelResults = useQueries({
    queries: channels.map((c) => ({
      queryKey: ['product-channel-assets', productId, c.id],
      queryFn: async () => (await getChannelImages(productId!, c.id)).slots ?? [],
      enabled: !!productId,
      staleTime: 30_000,
    })),
  })
  const aiRefQuery = useQuery({
    queryKey: ['product-ai-references', productId],
    queryFn: async () => (await getAIReferenceImages(productId!)).slots ?? [],
    enabled: !!productId,
    staleTime: 30_000,
  })

  const filledByChannel = useMemo(
    () => buildChannelMap(channels.map((c) => c.id), channelResults),
    [channels, channelResults],
  )
  const filledAiRef = useMemo(() => buildAiRefMap(aiRefQuery.data), [aiRefQuery.data])

  const isOccupied = useCallback(
    (t: AddToProductTarget) =>
      t.type === 'channel'
        ? !!filledByChannel[t.channelId]?.[t.slot]
        : !!filledAiRef[t.slot],
    [filledByChannel, filledAiRef],
  )

  return { isOccupied, filledByChannel, filledAiRef }
}
